import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, Lock } from "lucide-react";
import API from "../lib/api";
import { Section } from "./Common";
import OfferCard from "./OfferCard";

/**
 * Phase 6.4 — Homepage Member Benefits strip.
 *
 * Pulls the top 3 featured + active offers from /api/business/offers/featured
 * and renders them in a premium 3-up grid.  If no featured offers exist, the
 * whole section gracefully hides (avoids an empty placeholder on the home page).
 */
export default function MemberBenefitsStrip() {
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/business/offers/featured?limit=3")
      .then(({ data }) => setOffers(data.items || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && offers.length === 0) return null;

  return (
    <Section
      eyebrow={t("home.member_benefits_eyebrow", "— Member Benefits")}
      title={t("home.member_benefits_title", "Curated offers from partner businesses")}
      subtitle={t(
        "home.member_benefits_subtitle",
        "Exclusive discounts and trials from vetted businesses across the DONiTSH!T ecosystem. Free to join — sign in to reveal the codes."
      )}
    >
      {/* Header CTA row */}
      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex items-center gap-2 text-[11px] text-white/55">
          <Lock className="w-3 h-3 gold-text" /> {t("home.member_only", "Member-only — free to join")}
        </div>
        <Link
          to="/business/offers"
          className="text-xs gold-text inline-flex items-center gap-1 hover:underline"
          data-testid="home-member-benefits-all"
        >
          {t("home.member_benefits_all", "View all offers")} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card-surface aspect-[16/12] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-member-benefits">
          {offers.map((o) => (
            <OfferCard key={o.offer_id} offer={o} returnUrl="/" testidPrefix="home-offer" />
          ))}
        </div>
      )}
    </Section>
  );
}
